import { allowedNextStatuses, type Actor, type Ticket, type TicketStatus } from '@kilat/shared';

import { nextSubStage, type WashSubStage } from './queue-substage.js';

/**
 * Pure logic behind the washer's symbol-first queue (`components/floor/`).
 *
 * Kept out of the component so the decisions a washer's screen makes — which
 * tab a car sits in, what its ONE button does, which tab opens first — are
 * unit-testable without React, and so none of them is an inline
 * `status === '…'` check: every permission question goes to the canonical
 * transition table in `@kilat/shared` (`allowedNextStatuses`).
 */

export type FloorTab = 'waiting' | 'washing' | 'ready';

export const FLOOR_TABS: readonly FloorTab[] = ['waiting', 'washing', 'ready'];

/** Which tab a live ticket belongs to; null for anything not on the floor. */
const TAB_OF_STATUS: Partial<Record<TicketStatus, FloorTab>> = {
  inQueue: 'waiting',
  inWashingProcess: 'washing',
  readyForCollection: 'ready',
};

export function floorTabOf(status: TicketStatus): FloorTab | null {
  return TAB_OF_STATUS[status] ?? null;
}

/**
 * The moves the floor view offers. The transition table also lets a washer
 * mark a ready car `collected` — deliberately NOT offered here: /pos lists
 * `readyForCollection` tickets for payment, so a washer closing a car out from
 * the bay would remove it from the till before anyone took the money. The
 * cashier closes it where the payment is recorded.
 */
const FLOOR_FORWARD: ReadonlySet<TicketStatus> = new Set<TicketStatus>([
  'inWashingProcess',
  'readyForCollection',
]);

export type FloorMove =
  | { readonly kind: 'status'; readonly to: TicketStatus }
  | { readonly kind: 'subStage'; readonly to: WashSubStage };

/**
 * The single dominant action for a car on the floor, or null when the floor
 * offers none (a ready car, or a role the table refuses).
 *
 * With sub-stages on, finishing a wash first walks the stages: a car that has
 * just started is treated as already in the bay (starting it put it there), so
 * the washer's taps are drying → quality check → ready, never a redundant
 * "in bay" tap.
 */
export function floorMoveFor(
  ticket: Pick<Ticket, 'status' | 'subStage'>,
  actor: Actor,
  allowSubStages: boolean,
): FloorMove | null {
  const to = allowedNextStatuses(ticket.status, actor).find((s) => FLOOR_FORWARD.has(s));
  if (!to) return null;
  if (allowSubStages && to === 'readyForCollection') {
    const next = nextSubStage(ticket.subStage ?? 'inBay');
    if (next) return { kind: 'subStage', to: next };
  }
  return { kind: 'status', to };
}

/** Which tab to open on: the one with work in it, washing first. */
export function defaultFloorTab(counts: Readonly<Record<FloorTab, number>>): FloorTab {
  if (counts.washing > 0) return 'washing';
  if (counts.waiting > 0) return 'waiting';
  if (counts.ready > 0) return 'ready';
  return 'waiting';
}

/** Elapsed time as a clock reading — `0:07`, `1:25` — readable in any script. */
export function formatElapsed(ms: number): string {
  const mins = Math.max(0, Math.floor(ms / 60_000));
  return `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}`;
}

/** Same thresholds as the full board's elapsed chip: amber at 20 min, red at 40. */
export function elapsedTone(ms: number): 'calm' | 'warn' | 'danger' {
  const mins = Math.floor(ms / 60_000);
  if (mins >= 40) return 'danger';
  if (mins >= 20) return 'warn';
  return 'calm';
}

/**
 * The glyph on a bay button: the bay's number when its name has one
 * ("Bay 3 (detailing)" → "3"), otherwise its first two letters. The number IS
 * the symbol — it matches what is painted on the floor.
 */
export function bayGlyph(name: string): string {
  const digits = /\d+/.exec(name);
  if (digits) return digits[0];
  return name.trim().slice(0, 2).toUpperCase() || '?';
}

export type VehicleKind = 'sedan' | 'suv' | 'van' | 'bike' | 'other';

/** Map a free-form vehicle-type id (`sedan`, `suv`, `mpv`, `bike`…) to a silhouette. */
export function vehicleKindOf(vehicleType: string | undefined): VehicleKind {
  const v = (vehicleType ?? '').toLowerCase();
  if (!v) return 'other';
  if (v.includes('suv') || v.includes('4x4') || v.includes('pickup')) return 'suv';
  if (v.includes('mpv') || v.includes('van')) return 'van';
  if (v.includes('bike') || v.includes('motor')) return 'bike';
  if (v.includes('sedan') || v.includes('hatch') || v.includes('car') || v.includes('compact')) {
    return 'sedan';
  }
  return 'other';
}

/**
 * A stable colour per package, so "the blue one" means the same wash on every
 * card all shift. Derived from the package id (falling back to its name) and
 * drawn from colour ROLES only, so it themes with the app.
 */
export const PACKAGE_HUES = [
  'var(--color-info)',
  'var(--color-success)',
  'var(--color-warn)',
  'color-mix(in oklab, var(--color-info) 55%, var(--color-danger))',
  'color-mix(in oklab, var(--color-success) 50%, var(--color-info))',
] as const;

export function packageHue(key: string | undefined): string {
  if (!key) return 'var(--color-on-surface-muted)';
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return PACKAGE_HUES[h % PACKAGE_HUES.length] ?? PACKAGE_HUES[0];
}
